import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const BOT_RESPONSES = {
  greeting: {
    text: "Hello! 👋 I'm your YouthInfotech AI assistant. I can help you with:\n\n• Finding the right course\n• Study abroad guidance\n• Scholarship information\n• Booking counseling sessions\n\nWhat would you like to know?",
    quickReplies: ['Find a Course', 'Study Abroad', 'Scholarships', 'Book Counseling'],
  },
  courses: {
    text: "🎓 We offer programs across 6 major domains:\n\n**Medical** – MBBS, BDS, BAMS, BPT\n**Engineering** – BTech, MTech, Diploma\n**IT & CS** – BCA, MCA, AI, Data Science\n**Nursing** – BSc Nursing, GNM, ANM\n**Business** – BBA, MBA, PGDM\n**Study Abroad** – Global programs\n\nWhich area interests you most?",
    quickReplies: ['Medical Programs', 'Engineering', 'IT & Computer Science', 'Business & MBA'],
    link: { label: 'Browse All Courses →', to: '/courses' },
  },
  medical: {
    text: "🏥 **Medical Education Programs:**\n\n• MBBS in India – Top government & private colleges\n• MBBS Abroad – Russia, Uzbekistan, Philippines, Georgia\n• BDS – Dental Surgery\n• BAMS – Ayurvedic Medicine\n• BPT – Physiotherapy\n\nEligibility: 10+2 with Biology. NEET required for India.",
    quickReplies: ['MBBS in India', 'MBBS Abroad', 'Book Counseling'],
    link: { label: 'Medical Counseling →', to: '/medical-counseling' },
  },
  engineering: {
    text: "⚙️ **Engineering & Technology Programs:**\n\n• BTech – CSE, ECE, Mechanical, Civil\n• MTech – Specializations\n• Diploma Engineering – Polytechnic\n\nAdmission via JEE Main/Advanced or state entrance exams.",
    quickReplies: ['BTech Programs', 'Study in Germany', 'Book Counseling'],
    link: { label: 'Engineering Counseling →', to: '/engineering-counseling' },
  },
  it: {
    text: "💻 **IT & Computer Science Programs:**\n\n• BCA / MCA – Bachelor's & Master's\n• Artificial Intelligence & ML\n• Data Science & Analytics\n• Cyber Security\n• Cloud Computing\n\nHigh-demand careers with global opportunities!",
    quickReplies: ['Data Science', 'Cyber Security', 'Study Abroad for IT'],
    link: { label: 'Explore IT Courses →', to: '/courses?category=it' },
  },
  business: {
    text: "📊 **Business & Management Programs:**\n\n• BBA – Bachelor's in Business\n• MBA – Master's in Business Administration\n• PGDM – Post Graduate Diploma in Management\n• Specializations: Finance, Marketing, HR, Operations\n\nTop B-Schools in India & globally!",
    quickReplies: ['MBA in India', 'MBA Abroad', 'Book Counseling'],
    link: { label: 'Management Programs →', to: '/management-programs' },
  },
  abroad: {
    text: "🌍 **Study Abroad Destinations:**\n\n🇺🇸 USA – World-class research universities\n🇬🇧 UK – Oxford, Cambridge & top Russell Group\n🇨🇦 Canada – PR-friendly, affordable\n🇦🇺 Australia – Quality education + work rights\n🇩🇪 Germany – Low/no tuition fees!\n🇸🇬 Singapore – Asia's education hub\n\nWhich country interests you?",
    quickReplies: ['Study in USA', 'Study in UK', 'Study in Canada', 'Study in Germany'],
    link: { label: 'Study Abroad Hub →', to: '/study-abroad' },
  },
  usa: {
    text: "🇺🇸 **Study in USA:**\n\nTop Universities: MIT, Harvard, Stanford, Caltech\nAverage Tuition: $20,000–$55,000/year\nPrograms: All major fields\nWork: OPT – 1-3 years post-study\nVisa: F-1 Student Visa\n\nSAT/GRE/GMAT + English proficiency required.",
    quickReplies: ['UK Universities', 'Canada Info', 'Book Counseling'],
    link: { label: 'USA Guide →', to: '/country/usa' },
  },
  uk: {
    text: "🇬🇧 **Study in UK:**\n\nTop Universities: Oxford, Cambridge, Imperial, UCL\nTuition: £10,000–£38,000/year\nDuration: 3-year UG, 1-year PG\nWork: 2-year Graduate Route Visa\nVisa: Tier 4 Student Visa\n\nIELTS 6.0+ required.",
    quickReplies: ['USA Info', 'Australia Info', 'Book Counseling'],
    link: { label: 'UK Guide →', to: '/country/uk' },
  },
  canada: {
    text: "🇨🇦 **Study in Canada:**\n\nTop Universities: UofT, UBC, McGill, Waterloo\nTuition: CAD 15,000–40,000/year\nWork During Study: 20 hrs/week\nPost-Study: PGWP 1-3 years\nImmigration-friendly country!\n\nIELTS 6.5+ required.",
    quickReplies: ['UK Info', 'Australia Info', 'Book Counseling'],
    link: { label: 'Canada Guide →', to: '/country/canada' },
  },
  germany: {
    text: "🇩🇪 **Study in Germany:**\n\nPublic Universities: Often FREE!\nTuition: €0–€3,000/semester\nTop Institutions: TU Munich, Heidelberg, LMU\nLanguage: Some programs in English\nVisa: German Student Visa\n\nGerman language helpful but not always required.",
    quickReplies: ['Other European Countries', 'Australia Info', 'Book Counseling'],
    link: { label: 'Germany Guide →', to: '/country/germany' },
  },
  scholarships: {
    text: "🏆 **Scholarship Opportunities:**\n\n• Government Scholarships (ICCR, DAAD, Chevening)\n• University Merit Scholarships\n• Country-specific grants\n• Sports & Cultural scholarships\n• Need-based financial aid\n\nWe help you identify & apply for the best scholarships!",
    quickReplies: ['Country-specific Scholarships', 'Book Counseling', 'Study Abroad'],
    link: { label: 'Scholarships Database →', to: '/scholarships' },
  },
  counseling: {
    text: "📅 **Book Your Free Counseling Session!**\n\nOur expert counselors will:\n✅ Analyze your academic profile\n✅ Recommend best-fit courses\n✅ Guide on top universities\n✅ Scholarship assistance\n✅ Complete admission support\n\nSessions available online & in-person.",
    quickReplies: ['Schedule Now', 'Call Us Instead', 'WhatsApp Us'],
    link: { label: 'Book Free Session →', to: '/free-counseling' },
  },
  default: {
    text: "I understand you're looking for information. Let me connect you with our expert counselors who can provide personalized guidance!\n\n📞 Call: +91 83104 08977\n📧 Email: youthinfotech01@gmail.com\n💬 WhatsApp: Click the green button\n\nOr book a free session using the button below.",
    quickReplies: ['Find a Course', 'Study Abroad', 'Book Counseling'],
    link: { label: 'Book Free Session →', to: '/free-counseling' },
  },
};

function getResponse(message) {
  const msg = message.toLowerCase();
  if (msg.includes('medical') || msg.includes('mbbs') || msg.includes('bds') || msg.includes('doctor')) return BOT_RESPONSES.medical;
  if (msg.includes('engineering') || msg.includes('btech') || msg.includes('mtech')) return BOT_RESPONSES.engineering;
  if (msg.includes('it') || msg.includes('computer') || msg.includes('bca') || msg.includes('mca') || msg.includes('data science') || msg.includes('ai')) return BOT_RESPONSES.it;
  if (msg.includes('business') || msg.includes('mba') || msg.includes('management') || msg.includes('bba')) return BOT_RESPONSES.business;
  if (msg.includes('nursing') || msg.includes('paramedical')) return { text: '🩺 **Nursing & Paramedical:**\n\n• BSc Nursing\n• GNM Nursing\n• ANM Nursing\n• Radiology\n• Medical Lab Technology\n• Physiotherapy\n\nCombine healthcare passion with global opportunities!', quickReplies: ['Book Counseling', 'Study Abroad for Nursing'], link: { label: 'Nursing Programs →', to: '/nursing-programs' } };
  if (msg.includes('usa') || msg.includes('america') || msg.includes('united states')) return BOT_RESPONSES.usa;
  if (msg.includes('uk') || msg.includes('britain') || msg.includes('england') || msg.includes('oxford') || msg.includes('cambridge')) return BOT_RESPONSES.uk;
  if (msg.includes('canada')) return BOT_RESPONSES.canada;
  if (msg.includes('germany') || msg.includes('europe') || msg.includes('german')) return BOT_RESPONSES.germany;
  if (msg.includes('abroad') || msg.includes('international') || msg.includes('foreign')) return BOT_RESPONSES.abroad;
  if (msg.includes('scholarship') || msg.includes('financial') || msg.includes('fund')) return BOT_RESPONSES.scholarships;
  if (msg.includes('book') || msg.includes('counsel') || msg.includes('session') || msg.includes('appointment') || msg.includes('schedule')) return BOT_RESPONSES.counseling;
  if (msg.includes('course') || msg.includes('program') || msg.includes('study')) return BOT_RESPONSES.courses;
  if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey') || msg.includes('start')) return BOT_RESPONSES.greeting;
  return BOT_RESPONSES.default;
}

function Message({ msg }) {
  const isBot = msg.type === 'bot';
  return (
    <div className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-3`}>
      {isBot && (
        <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 mt-1"
          style={{ background: 'linear-gradient(135deg,#4f46e5,#9333ea)' }}>
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="3.2" fill="rgba(255,255,255,0.3)" stroke="white" strokeWidth="0.8"/>
            <circle cx="10" cy="5" r="1.2" fill="white"/>
            <circle cx="10" cy="15" r="1.2" fill="white"/>
            <circle cx="5" cy="10" r="1.2" fill="white"/>
            <circle cx="15" cy="10" r="1.2" fill="white"/>
            <circle cx="10" cy="10" r="1.4" fill="white"/>
          </svg>
        </div>
      )}
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${
        isBot
          ? 'bg-gray-50 text-gray-800 rounded-tl-sm border border-gray-100'
          : 'bg-navy-900 text-white rounded-tr-sm'
      }`}>
        {msg.text.split('\n').map((line, i) => (
          <span key={i}>
            {line.replace(/\*\*(.*?)\*\*/g, '$1')}
            {i < msg.text.split('\n').length - 1 && <br />}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AIAssistant() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, type: 'bot', text: t('ai.greeting') },
  ]);
  const [input, setInput] = useState('');
  const [quickReplies, setQuickReplies] = useState(BOT_RESPONSES.greeting.quickReplies);
  const [currentLink, setCurrentLink] = useState(null);
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const sendMessage = (text) => {
    if (!text.trim()) return;

    const userMsg = { id: Date.now(), type: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setTyping(true);
    setQuickReplies([]);
    setCurrentLink(null);

    setTimeout(() => {
      const response = getResponse(text);
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: response.text }]);
      setQuickReplies(response.quickReplies || []);
      setCurrentLink(response.link || null);
      setTyping(false);
    }, 700 + Math.random() * 600);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* AI Chat Button — LEFT SIDE */}
      <div className="fixed z-50 group"
        style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))', left: '1.5rem' }}>
        <button
          onClick={() => setOpen(o => !o)}
          aria-label="Open AI Assistant"
          className="relative flex items-center justify-center w-14 h-14 rounded-2xl transition-all duration-300 hover:scale-110"
          style={{
            background: open
              ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)'
              : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
            boxShadow: '0 0 20px rgba(139,92,246,0.55), 0 4px 20px rgba(99,102,241,0.4)',
          }}
        >
          <span className="absolute inset-0 rounded-2xl animate-ping opacity-20 pointer-events-none"
            style={{ background: 'linear-gradient(135deg,#6366f1,#9333ea)' }} />

          {open ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 relative z-10" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="5.5" fill="rgba(255,255,255,0.25)" stroke="white" strokeWidth="1.2"/>
              <circle cx="16" cy="7" r="2" fill="white"/>
              <circle cx="16" cy="25" r="2" fill="white"/>
              <circle cx="7" cy="16" r="2" fill="white"/>
              <circle cx="25" cy="16" r="2" fill="white"/>
              <circle cx="9.5" cy="9.5" r="1.5" fill="rgba(255,255,255,0.8)"/>
              <circle cx="22.5" cy="9.5" r="1.5" fill="rgba(255,255,255,0.8)"/>
              <circle cx="9.5" cy="22.5" r="1.5" fill="rgba(255,255,255,0.8)"/>
              <circle cx="22.5" cy="22.5" r="1.5" fill="rgba(255,255,255,0.8)"/>
              <line x1="16" y1="9" x2="16" y2="10.5" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="16" y1="21.5" x2="16" y2="23" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="9" y1="16" x2="10.5" y2="16" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="21.5" y1="16" x2="23" y2="16" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="10.5" y1="10.5" x2="11.8" y2="11.8" stroke="rgba(255,255,255,0.7)" strokeWidth="1" strokeLinecap="round"/>
              <line x1="20.2" y1="11.8" x2="21.5" y2="10.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1" strokeLinecap="round"/>
              <line x1="10.5" y1="21.5" x2="11.8" y2="20.2" stroke="rgba(255,255,255,0.7)" strokeWidth="1" strokeLinecap="round"/>
              <line x1="20.2" y1="20.2" x2="21.5" y2="21.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1" strokeLinecap="round"/>
              <circle cx="16" cy="16" r="2.2" fill="white"/>
            </svg>
          )}

          {!open && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-[9px] flex items-center justify-center font-bold animate-pulse z-20"
              style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)', boxShadow: '0 0 8px rgba(245,158,11,0.6)', fontSize: '9px' }}>
              AI
            </span>
          )}
        </button>

        {/* Hover label */}
        <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <div className="bg-gray-900 text-white text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
            ✨ AI Assistant
          </div>
        </div>
      </div>

      {/* Chat Panel — opens from LEFT */}
      {open && (
        <div className="fixed z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up"
          style={{
            bottom: 'calc(max(1.5rem, env(safe-area-inset-bottom, 1.5rem)) + 4.5rem)',
            left: '1rem',
            right: '1rem',
            maxWidth: '24rem',
            maxHeight: '70vh',
            boxShadow: '0 25px 60px rgba(99,102,241,0.2), 0 0 0 1px rgba(139,92,246,0.15)'
          }}>

          {/* Header */}
          <div className="px-4 py-3.5 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)' }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="4" fill="rgba(255,255,255,0.3)" stroke="white" strokeWidth="1"/>
                <circle cx="16" cy="8" r="1.5" fill="white"/>
                <circle cx="16" cy="24" r="1.5" fill="white"/>
                <circle cx="8" cy="16" r="1.5" fill="white"/>
                <circle cx="24" cy="16" r="1.5" fill="white"/>
                <circle cx="10.5" cy="10.5" r="1.2" fill="rgba(255,255,255,0.8)"/>
                <circle cx="21.5" cy="10.5" r="1.2" fill="rgba(255,255,255,0.8)"/>
                <circle cx="10.5" cy="21.5" r="1.2" fill="rgba(255,255,255,0.8)"/>
                <circle cx="21.5" cy="21.5" r="1.2" fill="rgba(255,255,255,0.8)"/>
                <circle cx="16" cy="16" r="1.8" fill="white"/>
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-white font-heading font-semibold text-sm">{t('ai.title')}</div>
              <div className="text-white/70 text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" />
                Online • YouthInfotech AI
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4" style={{ minHeight: 0 }}>
            {messages.map(msg => <Message key={msg.id} msg={msg} />)}

            {typing && (
              <div className="flex justify-start mb-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-2 flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#4f46e5,#9333ea)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="3.2" fill="rgba(255,255,255,0.3)" stroke="white" strokeWidth="0.8"/>
                  <circle cx="10" cy="5" r="1.2" fill="white"/>
                  <circle cx="10" cy="15" r="1.2" fill="white"/>
                  <circle cx="5" cy="10" r="1.2" fill="white"/>
                  <circle cx="15" cy="10" r="1.2" fill="white"/>
                  <circle cx="10" cy="10" r="1.4" fill="white"/>
                </svg>
              </div>
              <div className="bg-gray-50 border border-purple-100 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1.5">
                {[0, 1, 2].map(i => (
                  <span key={i} className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'linear-gradient(135deg,#7c3aed,#9333ea)', animationDelay: `${i * 0.15}s` }} />
                ))}
                </div>
              </div>
            )}

            {/* Quick Replies */}
            {!typing && quickReplies.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 mb-3">
                {quickReplies.map(reply => (
                  <button
                    key={reply}
                    onClick={() => sendMessage(reply)}
                    className="text-xs bg-purple-50 text-purple-800 border border-purple-100 hover:bg-purple-700 hover:text-white hover:border-purple-700 px-3 py-1.5 rounded-full transition-all duration-200 font-medium"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

            {/* Page Link */}
            {!typing && currentLink && (
              <div className="mb-3">
                <Link
                  to={currentLink.to}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 hover:text-navy-900 bg-navy-50 hover:bg-navy-100 px-3 py-2 rounded-lg transition-colors"
                >
                  {currentLink.label}
                </Link>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-gray-100 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={t('ai.placeholder')}
              className="flex-1 text-sm border border-purple-100 rounded-xl px-3 py-2 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 bg-gray-50"
            />
            <button
              type="submit"
              disabled={!input.trim()}
              className="text-white px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-40 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#4f46e5,#9333ea)' }}
            >
              {t('ai.send')}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
